import * as React from "react";
import { useHistory } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { URLS, useAccountSettings, useSocialCoachSession } from "@/utils";
import { PageWrapper } from "@/components/PageWrapper";
import { useMemo, useState } from "react";
import { CoachComponents, WebComponents } from "@/interfaces";
import { AssistantForm } from "@/components/AssistantForm";
import { createAssistantSchema, CreateAssistantFormValues } from "@/components/AssistantForm/schemas";
import { AssistantCoachServices } from "@/services/bedrock/AssistantCoachServices";
import { toast } from "sonner";

const CreateAssistantFC: React.FC = () => {
  const history = useHistory();
  const { coachId } = useSocialCoachSession();
  const [loading, setLoading] = useState<boolean>(false);

  const goToAssistantsList = () => {
    setLoading(false);
    history.push(URLS.coach.assistants);
  };

  const initialPermissions = useMemo(() => {
    return Object.values(CoachComponents).reduce((a, p) => {
      return { ...a, [p.id]: "WRITE" };
    }, {});
  }, []);

  const form = useForm<CreateAssistantFormValues>({
    resolver: zodResolver(createAssistantSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      branches: [],
      regions: [],
      explicitPlayerIds: [],
      isScoped: false,
      permissionsMap: initialPermissions,
    },
  });

  const onSubmit = async (values: CreateAssistantFormValues) => {
    const { firstName, lastName, email, branches, regions, explicitPlayerIds, isScoped } = values;
    setLoading(true);
    try {
      const branchIds = (branches ?? []).map(branchId => Number(branchId));
      const regionIds = (regions ?? []).map(regionId => Number(regionId));

      await AssistantCoachServices.createAssistantCoach({
        firstName: firstName!,
        lastName: lastName!,
        email: email!,
        branchIds,
        regionIds,
        explicitPlayerIds,
        isScoped,
        componentPermissions: values.permissionsMap,
      });
      toast.success("Assistant created successfully");
      goToAssistantsList();
    } catch (e: any) {
      console.error(e);
      form.setError("email", { message: e?.message || String(e) || "An error occurred" });
      setLoading(false);
    }
  };

  const actions = useMemo(() => {
    return [{ text: "Save", onClick: form.handleSubmit(onSubmit), loading: loading }];
  }, [loading, form]);

  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.ASSISTANTS);

  return (
    <PageWrapper
      title="Create a New Assistant"
      breadcrumbs={[{ name: "Users", link: URLS.coach.assistants }]}
      actions={actions}
    >
      <AssistantForm form={form} coachId={coachId!} withWritePermission={withWritePermission} />
    </PageWrapper>
  );
};

export const CreateAssistant = CreateAssistantFC;
